/**
 * Admin Staff Management Page
 * Full CRUD: Add, Edit, Delete, Disable, Reset Password/PIN, Send Credentials
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Users,
  Search,
  RefreshCw,
  Plus,
  Trash2,
  Ban,
  Play,
  X,
  Key,
  Lock,
  Shield,
  Eye,
  EyeOff,
  CheckCircle,
  Package,
  Truck,
  Headphones,
  DollarSign,
  FileText,
  Tag,
  Building,
  Edit,
  Send,
  Mail
} from 'lucide-react';

const FUNCTION_ICONS = {
  inventory: Package,
  orders: FileText,
  drivers: Truck,
  support: Headphones,
  finance: DollarSign,
  audit: FileText,
  pricing: Tag,
  businesses: Building,
};

const FUNCTION_COLORS = {
  inventory: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  orders: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  drivers: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  support: 'bg-green-500/20 text-green-400 border-green-500/30',
  finance: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  audit: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  pricing: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  businesses: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
};

const AdminStaffManagement = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState(null);
  const [resetType, setResetType] = useState('pin');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [showPin, setShowPin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [staffForm, setStaffForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    telephone: '',
    pin: '',
    password: '',
    assigned_functions: []
  });
  
  const [resetForm, setResetForm] = useState({
    new_value: '',
    admin_pin: '',
    reason: ''
  });

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }
    fetchStaff();
    fetchFunctions();
  }, [isAdmin, navigate]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/staff/');
      setStaff(response.data || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const fetchFunctions = async () => {
    try {
      const response = await api.get('/admin/staff/functions');
      setFunctions(response.data?.functions || []);
    } catch (error) {
      console.error('Error fetching functions:', error);
    }
  };

  const resetFormData = () => {
    setStaffForm({
      first_name: '',
      last_name: '',
      email: '',
      telephone: '',
      pin: '',
      password: '',
      assigned_functions: []
    });
  };

  const openAddModal = () => {
    resetFormData();
    setShowAddModal(true);
  };

  const openEditModal = (member) => {
    setSelectedStaff(member);
    setStaffForm({
      first_name: member.first_name || member.name?.split(' ')[0] || '',
      last_name: member.last_name || member.name?.split(' ').slice(1).join(' ') || '',
      email: member.email || '',
      telephone: member.telephone || member.phone || '',
      pin: '',
      password: '',
      assigned_functions: member.assigned_functions || []
    });
    setShowEditModal(true);
  };

  const handleAddStaff = async () => {
    if (!staffForm.first_name || !staffForm.last_name || !staffForm.email || !staffForm.pin || !staffForm.password) {
      toast.error('First name, last name, email, PIN, and password are required');
      return;
    }
    if (staffForm.pin.length < 4 || staffForm.pin.length > 6 || !/^\d+$/.test(staffForm.pin)) {
      toast.error('PIN must be 4-6 digits');
      return;
    }
    
    setActionLoading('add');
    try {
      await api.post('/admin/staff/', staffForm);
      toast.success('Staff member added successfully!');
      setShowAddModal(false);
      resetFormData();
      fetchStaff();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to add staff"; toast.error(errorMsg, { duration: 5000 }); if (errorMsg.toLowerCase().includes("email")) { setStaffForm(prev => ({...prev, email: ""})); };
    } finally {
      setActionLoading(null);
    }
  };

  const handleEditStaff = async () => {
    if (!staffForm.first_name || !staffForm.last_name || !staffForm.email) {
      toast.error('First name, last name, and email are required');
      return;
    }
    
    setActionLoading('edit');
    try {
      await api.put(`/admin/staff/${selectedStaff.id}`, {
        first_name: staffForm.first_name,
        last_name: staffForm.last_name,
        email: staffForm.email,
        telephone: staffForm.telephone,
        assigned_functions: staffForm.assigned_functions
      });
      toast.success('Staff member updated successfully!');
      setShowEditModal(false);
      setSelectedStaff(null);
      resetFormData();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update staff');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisable = async (userId) => {
    const reason = prompt('Enter reason for disabling this staff member:');
    if (!reason) return;
    
    setActionLoading(userId);
    try {
      await api.post(`/admin/staff/${userId}/disable`, { reason });
      toast.success('Staff member disabled');
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable staff');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnable = async (userId) => {
    setActionLoading(userId);
    try {
      await api.post(`/admin/staff/${userId}/enable`);
      toast.success('Staff member enabled');
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable staff');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (userId) => {
    const adminPin = prompt('Enter your admin PIN to confirm deletion:');
    if (!adminPin) return;
    const reason = prompt('Enter reason for deleting this staff member:');
    if (!reason) return;
    
    setActionLoading(userId);
    try {
      await api.delete(`/admin/staff/${userId}`, { data: { admin_pin: adminPin, reason } });
      toast.success('Staff member deleted');
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete staff');
    } finally {
      setActionLoading(null);
    }
  };

  const openResetModal = (staffMember, type) => {
    setSelectedStaff(staffMember);
    setResetType(type);
    setResetForm({ new_value: '', admin_pin: '', reason: '' });
    setShowResetModal(true);
  };

  const handleReset = async () => {
    if (!resetForm.new_value || !resetForm.admin_pin || !resetForm.reason) {
      toast.error('All fields are required');
      return;
    }
    
    setActionLoading('reset');
    try {
      const endpoint = resetType === 'pin' ? 'reset-pin' : 'reset-password';
      const payload = {
        [resetType === 'pin' ? 'new_pin' : 'new_password']: resetForm.new_value,
        admin_pin: resetForm.admin_pin,
        reason: resetForm.reason
      };
      await api.post(`/admin/staff/${selectedStaff.id}/${endpoint}`, payload);
      toast.success(`${resetType === 'pin' ? 'PIN' : 'Password'} reset successfully!`);
      setShowResetModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset');
    } finally {
      setActionLoading(null);
    }
  };

  const openCredentialsModal = (staffMember) => {
    setSelectedStaff(staffMember);
    setShowCredentialsModal(true);
    setEmailPreviewData(null);
  };

  const handlePreviewEmail = async () => {
    setActionLoading('preview');
    try {
      const response = await api.post('/password-reset/send-credentials', {
        user_id: selectedStaff.id,
        preview: true
      });
      setEmailPreviewData(response.data);
      setShowEmailPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview email');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendCredentials = async () => {
    setActionLoading('send');
    try {
      const response = await api.post('/password-reset/send-credentials', {
        user_id: selectedStaff.id,
        preview: false
      });
      
      if (response.data.email_sent) {
        toast.success(`Email Sent Successfully! Credentials sent to ${response.data.recipient}`, { duration: 5000 });
      } else {
        toast.warning(`Email failed. Temporary password: ${response.data.temp_password}`);
      }
      
      setShowEmailPreview(false);
      setShowCredentialsModal(false);
      setSelectedStaff(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send credentials');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleFunction = (funcId) => {
    setStaffForm(prev => ({
      ...prev,
      assigned_functions: prev.assigned_functions.includes(funcId)
        ? prev.assigned_functions.filter(f => f !== funcId)
        : [...prev.assigned_functions, funcId]
    }));
  };

  const filteredStaff = staff.filter(s => {
    const fullName = s.first_name && s.last_name ? `${s.first_name} ${s.last_name}` : s.name || '';
    return fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.staff_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.telephone?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const stats = {
    total: staff.length,
    active: staff.filter(s => s.is_active !== false).length,
    disabled: staff.filter(s => s.is_active === false).length,
  };

  const getStaffName = (member) => {
    return member.first_name && member.last_name 
      ? `${member.first_name} ${member.last_name}` 
      : member.name || 'Unknown';
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold tracking-tight">Liantiao</span>
              <span className="text-xs font-mono text-purple-400 bg-purple-500/10 px-2 py-1 rounded border border-purple-500/20">
                STAFF
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={openAddModal}
              className="bg-primary text-primary-foreground"
              data-testid="add-staff-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Staff
            </Button>
            <Button
              onClick={fetchStaff}
              variant="outline"
              size="sm"
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              <span className="text-muted-foreground text-sm">Total Staff</span>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="h-5 w-5 text-teal-400" />
              <span className="text-muted-foreground text-sm">Active</span>
            </div>
            <p className="text-2xl font-bold text-teal-400">{stats.active}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Ban className="h-5 w-5 text-red-400" />
              <span className="text-muted-foreground text-sm">Disabled</span>
            </div>
            <p className="text-2xl font-bold text-red-400">{stats.disabled}</p>
          </div>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, or staff ID..."
            className="pl-10"
            data-testid="search-input"
          />
        </div>

        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="staff-table">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide p-4">Staff</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide p-4">Functions</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide p-4">Status</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center">
                      <RefreshCw className="h-6 w-6 text-primary animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : filteredStaff.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-muted-foreground">
                      No staff members found
                    </td>
                  </tr>
                ) : (
                  filteredStaff.map((member) => (
                    <tr 
                      key={member.id}
                      className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                      data-testid={`staff-row-${member.id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <Users className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{getStaffName(member)}</p>
                            <p className="text-xs text-muted-foreground">{member.email}</p>
                            <p className="text-xs text-muted-foreground">{member.telephone || member.phone || ''}</p>
                            <p className="text-xs text-muted-foreground font-mono">{member.staff_id || 'N/A'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {(member.assigned_functions || []).map((func) => {
                            const Icon = FUNCTION_ICONS[func] || Shield;
                            return (
                              <span 
                                key={func}
                                className={`px-2 py-0.5 rounded text-xs border flex items-center gap-1 ${FUNCTION_COLORS[func] || 'bg-muted text-muted-foreground'}`}
                              >
                                <Icon className="h-3 w-3" />
                                {func}
                              </span>
                            );
                          })}
                          {(!member.assigned_functions || member.assigned_functions.length === 0) && (
                            <span className="text-muted-foreground text-xs">No functions assigned</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-mono border ${
                          member.is_active !== false
                            ? 'bg-teal-500/20 text-teal-400 border-teal-500/30'
                            : 'bg-red-500/20 text-red-400 border-red-500/30'
                        }`}>
                          {member.is_active !== false ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <Button 
                            onClick={() => openEditModal(member)} 
                            variant="ghost" 
                            size="sm" 
                            className="text-blue-400 hover:bg-blue-500/10" 
                            title="Edit Staff"
                            data-testid={`edit-${member.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            onClick={() => openCredentialsModal(member)} 
                            variant="ghost" 
                            size="sm" 
                            className="text-emerald-400 hover:bg-emerald-500/10" 
                            title="Send Login Credentials"
                            data-testid={`send-credentials-${member.id}`}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button 
                            onClick={() => openResetModal(member, 'pin')} 
                            variant="ghost" 
                            size="sm" 
                            className="text-muted-foreground hover:text-foreground" 
                            title="Reset PIN"
                            data-testid={`reset-pin-${member.id}`}
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button 
                            onClick={() => openResetModal(member, 'password')} 
                            variant="ghost" 
                            size="sm" 
                            className="text-muted-foreground hover:text-foreground" 
                            title="Reset Password"
                            data-testid={`reset-password-${member.id}`}
                          >
                            <Lock className="h-4 w-4" />
                          </Button>
                          {member.is_active !== false ? (
                            <Button 
                              onClick={() => handleDisable(member.id)} 
                              variant="ghost" 
                              size="sm" 
                              className="text-orange-400 hover:bg-orange-500/10" 
                              title="Disable"
                              disabled={actionLoading === member.id}
                              data-testid={`disable-${member.id}`}
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button 
                              onClick={() => handleEnable(member.id)} 
                              variant="ghost" 
                              size="sm" 
                              className="text-teal-400 hover:bg-teal-500/10" 
                              title="Enable"
                              disabled={actionLoading === member.id}
                              data-testid={`enable-${member.id}`}
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            onClick={() => handleDelete(member.id)} 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-400 hover:bg-red-500/10" 
                            title="Delete"
                            disabled={actionLoading === member.id}
                            data-testid={`delete-${member.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Add Staff Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" data-testid="add-staff-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Add New Staff
              </h2>
              <Button onClick={() => setShowAddModal(false)} variant="ghost" size="sm">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={staffForm.first_name}
                    onChange={(e) => setStaffForm({ ...staffForm, first_name: e.target.value })}
                    placeholder="John"
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={staffForm.last_name}
                    onChange={(e) => setStaffForm({ ...staffForm, last_name: e.target.value })}
                    placeholder="Doe"
                    data-testid="input-last-name"
                  />
                </div>
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={staffForm.email}
                  onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                  placeholder="staff@liantiao.com"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label>Telephone</Label>
                <Input
                  value={staffForm.telephone}
                  onChange={(e) => setStaffForm({ ...staffForm, telephone: e.target.value })}
                  placeholder="+1 876 xxx xxxx"
                  data-testid="input-telephone"
                />
              </div>
              <div>
                <Label>PIN (4-6 digits) *</Label>
                <div className="relative">
                  <Input
                    type={showPin ? 'text' : 'password'}
                    value={staffForm.pin}
                    onChange={(e) => setStaffForm({ ...staffForm, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    placeholder="••••"
                    maxLength={6}
                    data-testid="input-pin"
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPin(!showPin)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Password *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={staffForm.password}
                    onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="input-password"
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Assigned Functions</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {functions.map((func) => {
                    const Icon = FUNCTION_ICONS[func.id] || Shield;
                    const isSelected = staffForm.assigned_functions.includes(func.id);
                    return (
                      <button
                        key={func.id}
                        type="button"
                        onClick={() => toggleFunction(func.id)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-muted-foreground'
                        }`}
                        data-testid={`func-${func.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                          <span className={`text-sm font-medium ${isSelected ? 'text-primary' : ''}`}>{func.name}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{func.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowAddModal(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleAddStaff} className="flex-1 bg-primary" disabled={actionLoading === 'add'} data-testid="submit-add-staff">
                {actionLoading === 'add' ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Add Staff
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditModal && selectedStaff && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" data-testid="edit-staff-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-400" />
                Edit Staff
              </h2>
              <Button onClick={() => { setShowEditModal(false); setSelectedStaff(null); }} variant="ghost" size="sm">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={staffForm.first_name}
                    onChange={(e) => setStaffForm({ ...staffForm, first_name: e.target.value })}
                    placeholder="John"
                    data-testid="edit-first-name"
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={staffForm.last_name}
                    onChange={(e) => setStaffForm({ ...staffForm, last_name: e.target.value })}
                    placeholder="Doe"
                    data-testid="edit-last-name"
                  />
                </div>
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={staffForm.email}
                  onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                  placeholder="staff@liantiao.com"
                  data-testid="edit-email"
                />
              </div>
              <div>
                <Label>Telephone</Label>
                <Input
                  value={staffForm.telephone}
                  onChange={(e) => setStaffForm({ ...staffForm, telephone: e.target.value })}
                  placeholder="+1 876 xxx xxxx"
                  data-testid="edit-telephone"
                />
              </div>
              <div>
                <Label>Assigned Functions</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {functions.map((func) => {
                    const Icon = FUNCTION_ICONS[func.id] || Shield;
                    const isSelected = staffForm.assigned_functions.includes(func.id);
                    return (
                      <button
                        key={func.id}
                        type="button"
                        onClick={() => toggleFunction(func.id)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-muted-foreground'
                        }`}
                        data-testid={`edit-func-${func.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                          <span className={`text-sm font-medium ${isSelected ? 'text-primary' : ''}`}>{func.name}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{func.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button onClick={() => { setShowEditModal(false); setSelectedStaff(null); }} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleEditStaff} className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={actionLoading === 'edit'} data-testid="submit-edit-staff">
                {actionLoading === 'edit' ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset PIN/Password Modal */}
      {showResetModal && selectedStaff && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md p-6" data-testid="reset-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                {resetType === 'pin' ? <Key className="h-5 w-5 text-primary" /> : <Lock className="h-5 w-5 text-primary" />}
                Reset {resetType === 'pin' ? 'PIN' : 'Password'}
              </h2>
              <Button onClick={() => setShowResetModal(false)} variant="ghost" size="sm">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <p className="text-muted-foreground text-sm mb-4">
              Resetting {resetType} for: <strong>{getStaffName(selectedStaff)}</strong>
            </p>
            
            <div className="space-y-4">
              <div>
                <Label>New {resetType === 'pin' ? 'PIN (4-6 digits)' : 'Password'} *</Label>
                <Input
                  type={resetType === 'pin' ? 'text' : 'password'}
                  value={resetForm.new_value}
                  onChange={(e) => setResetForm({ ...resetForm, new_value: resetType === 'pin' ? e.target.value.replace(/\D/g, '').slice(0, 6) : e.target.value })}
                  placeholder={resetType === 'pin' ? '••••' : '••••••••'}
                  data-testid="input-new-value"
                />
              </div>
              <div>
                <Label>Your Admin PIN *</Label>
                <Input
                  type="password"
                  value={resetForm.admin_pin}
                  onChange={(e) => setResetForm({ ...resetForm, admin_pin: e.target.value })}
                  placeholder="Enter your PIN"
                  data-testid="input-admin-pin"
                />
              </div>
              <div>
                <Label>Reason *</Label>
                <Input
                  value={resetForm.reason}
                  onChange={(e) => setResetForm({ ...resetForm, reason: e.target.value })}
                  placeholder="e.g., Staff forgot PIN"
                  data-testid="input-reason"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowResetModal(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleReset} className="flex-1 bg-primary" disabled={actionLoading === 'reset'} data-testid="submit-reset">
                {actionLoading === 'reset' ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Reset {resetType === 'pin' ? 'PIN' : 'Password'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Send Credentials Modal */}
      {showCredentialsModal && selectedStaff && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md p-6" data-testid="credentials-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Mail className="h-5 w-5 text-emerald-400" />
                Send Login Credentials
              </h2>
              <Button onClick={() => { setShowCredentialsModal(false); setSelectedStaff(null); }} variant="ghost" size="sm">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-2">Staff Member</p>
                <p className="font-medium">{getStaffName(selectedStaff)}</p>
                <p className="text-sm text-muted-foreground">{selectedStaff.email}</p>
              </div>
              
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <p className="text-sm text-amber-200">
                  This will generate a new temporary password and send it to the staff member's email. 
                  They will be required to change their password on first login.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button onClick={() => { setShowCredentialsModal(false); setSelectedStaff(null); }} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handlePreviewEmail} 
                variant="outline" 
                className="flex-1 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" 
                disabled={actionLoading === 'preview'}
                data-testid="preview-email-btn"
              >
                {actionLoading === 'preview' ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                Preview Email
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview Modal */}
      {showEmailPreview && emailPreviewData && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
          <div className="bg-slate-900 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-slate-700">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between bg-slate-900">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Eye className="h-5 w-5 text-emerald-400" />
                Email Preview
              </h3>
              <Button variant="ghost" onClick={() => setShowEmailPreview(false)} className="p-2 text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[55vh]">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-slate-400">To:</span>
                  <p className="text-white">{emailPreviewData.recipient}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-400">Subject:</span>
                  <p className="text-white">{emailPreviewData.subject}</p>
                </div>
              </div>
              <div>
                <span className="text-sm text-slate-400">Temporary Password:</span>
                <p className="text-emerald-400 font-mono text-lg bg-slate-800 px-3 py-2 rounded mt-1">{emailPreviewData.temp_password || emailPreviewData.new_password}</p>
              </div>
              <div className="border border-slate-700 rounded-lg overflow-hidden bg-white">
                <div dangerouslySetInnerHTML={{ __html: emailPreviewData.html_content }} />
              </div>
            </div>
            <div className="p-4 border-t border-slate-800 flex justify-end gap-3 bg-slate-900">
              <Button variant="ghost" onClick={() => setShowEmailPreview(false)} className="text-slate-400">
                Cancel
              </Button>
              <Button 
                onClick={handleSendCredentials} 
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={actionLoading === 'send'}
                data-testid="confirm-send-email-btn"
              >
                {actionLoading === 'send' ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Email
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStaffManagement;
